import React from "react";
import { Helmet } from "react-helmet-async";
import "../assets/style/PrivacyPolicy.css";

// Public, static page: Google Play and the App Store link here, so it must load
// without logging in. Keep it in step with what the backend and the app really
// collect (models.py Users, account_deletion.py, worker.py emails, push_notifications.py).
const UPDATED_AT = "15 października 2026";
const CONTACT_EMAIL = "natchnieniestudio@gmail.com";

const Section = ({ title, children }) => (
  <section className="privacy-section">
    <h2>{title}</h2>
    {children}
  </section>
);

const PrivacyPolicy = () => (
  <div className="privacy-container">
    <Helmet>
      <title>Polityka prywatności | Natchnienie Studio</title>
      <meta
        name="description"
        content="Polityka prywatności strony natchnieniestudio.pl i aplikacji mobilnej Natchnienie Studio - jakie dane zbieramy, w jakim celu, komu je przekazujemy i jakie masz prawa."
      />
      <link rel="canonical" href="https://www.natchnieniestudio.pl/polityka-prywatnosci" />
    </Helmet>

    <h1>Polityka prywatności</h1>
    <p className="privacy-updated">Ostatnia aktualizacja: {UPDATED_AT}</p>

    <p>
      Ta polityka opisuje, jak przetwarzamy dane osobowe użytkowników strony
      internetowej <strong>www.natchnieniestudio.pl</strong> oraz aplikacji
      mobilnej <strong>Natchnienie Studio</strong> (Android i iOS), dalej razem
      „Serwis”. Strona i aplikacja korzystają z tego samego konta i tych samych
      danych.
    </p>

    <Section title="1. Administrator danych">
      <p>
        Administratorem danych osobowych jest Natalia Kotłowska, prowadząca
        działalność gospodarczą pod nazwą Natchnienie Studio, ul. Do Dworu 13,
        83-330 Otomino, NIP 5891856721, REGON 524946585.
      </p>
      <p>
        We wszystkich sprawach dotyczących danych osobowych możesz pisać na
        adres <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>.
      </p>
    </Section>

    <Section title="2. Jakie dane zbieramy">
      <p>Zbieramy tylko dane potrzebne do zapisów na zajęcia i rozliczeń:</p>
      <ul>
        <li>
          <strong>Dane konta:</strong> imię i nazwisko, adres e-mail, numer
          telefonu, hasło (przechowywane wyłącznie przez Firebase Authentication,
          nie mamy do niego dostępu). Przy logowaniu przez Google lub Apple
          otrzymujemy od tych dostawców imię i nazwisko oraz adres e-mail
          (w przypadku Apple może to być adres zastępczy).
        </li>
        <li>
          <strong>Dane o korzystaniu z usług studia:</strong> zapisy na zajęcia,
          listy rezerwowe, obecność, karnety, historia płatności i zaległości.
        </li>
        <li>
          <strong>Dane płatności:</strong> kwota, status i identyfikator
          transakcji. Danych karty ani logowania do banku nie otrzymujemy –
          podajesz je bezpośrednio operatorowi płatności PayU.
        </li>
        <li>
          <strong>Znajomi i wspólne zapisy:</strong> lista Twoich znajomych w
          Serwisie, zaproszenia oraz zgody na zapisywanie się nawzajem na
          zajęcia i korzystanie z karnetów.
        </li>
        <li>
          <strong>Tablica (aplikacja):</strong> wpisy o ukończonych zajęciach
          (rodzaj zajęć, data, statystyki treningów, opcjonalna ocena „Jak
          było?”) oraz reakcje znajomych.
        </li>
        <li>
          <strong>Dane urządzenia (aplikacja):</strong> token powiadomień push
          i system operacyjny (Android/iOS), potrzebne do wysyłania powiadomień.
        </li>
        <li>
          <strong>Statystyki korzystania z aplikacji:</strong> które ekrany
          otwierasz i jakie czynności wykonujesz (np. zapis na zajęcia,
          rozpoczęcie płatności, wpis na Tablicy), wersja aplikacji, model
          urządzenia i system, przybliżony kraj i miasto ustalone na podstawie
          adresu IP oraz Twój identyfikator w naszej bazie. Zbieramy je przez
          Google Analytics dla Firebase. Nie zbieramy identyfikatora
          reklamowego urządzenia i nie używamy tych danych do reklam.
        </li>
        <li>
          <strong>Dane techniczne:</strong> adres IP, data i godzina żądania
          oraz informacje o przeglądarce zapisywane w logach serwera w celach
          bezpieczeństwa i diagnostyki.
        </li>
      </ul>
      <p>
        Nie zbieramy dokładnej lokalizacji, kontaktów z telefonu ani danych o
        zdrowiu. Aplikacja nie zawiera narzędzi reklamowych ani śledzących Cię
        w innych aplikacjach i serwisach.
      </p>
    </Section>

    <Section title="3. Aparat i zdjęcia (aplikacja)">
      <p>
        Aplikacja prosi o dostęp do aparatu lub galerii tylko wtedy, gdy sam
        zechcesz dodać zdjęcie jako tło grafiki z treningiem do udostępnienia
        (np. w relacji na Instagramie). Zdjęcie jest przetwarzane wyłącznie na
        Twoim telefonie – nie wysyłamy go na nasz serwer. Gotową grafikę
        udostępniasz sam przez systemowe menu udostępniania; od tej chwili jej
        dalsze przetwarzanie podlega zasadom wybranej przez Ciebie aplikacji.
      </p>
    </Section>

    <Section title="4. Cele i podstawy prawne przetwarzania">
      <ul>
        <li>
          Założenie i prowadzenie konta, zapisy na zajęcia, sprzedaż karnetów,
          obsługa płatności, wysyłka potwierdzeń i przypomnień o zajęciach oraz
          płatnościach (e-mail, powiadomienia push) – wykonanie umowy
          (art. 6 ust. 1 lit. b RODO).
        </li>
        <li>
          Prowadzenie księgowości i przechowywanie dokumentów rozliczeniowych –
          obowiązek prawny (art. 6 ust. 1 lit. c RODO).
        </li>
        <li>
          Funkcje społecznościowe (znajomi, wspólne zapisy, Tablica) – wykonanie
          umowy w zakresie funkcji, z których zdecydujesz się korzystać
          (art. 6 ust. 1 lit. b RODO). Automatyczne publikowanie wpisów na
          Tablicy jest domyślnie wyłączone i możesz je w każdej chwili zmienić
          w profilu.
        </li>
        <li>
          Bezpieczeństwo Serwisu, kopie zapasowe, dochodzenie i obrona przed
          roszczeniami – nasz prawnie uzasadniony interes (art. 6 ust. 1 lit. f
          RODO).
        </li>
        <li>
          Statystyki korzystania z aplikacji, żeby wiedzieć, które funkcje są
          używane i gdzie coś nie działa – nasz prawnie uzasadniony interes
          (art. 6 ust. 1 lit. f RODO). Możesz wnieść sprzeciw, pisząc do nas;
          wtedy wyłączymy zbieranie statystyk dla Twojego konta.
        </li>
      </ul>
      <p>
        Podanie danych jest dobrowolne, ale bez imienia i nazwiska, adresu
        e-mail i numeru telefonu nie możemy założyć konta ani zapisać Cię na
        zajęcia.
      </p>
    </Section>

    <Section title="5. Kto widzi Twoje dane">
      <ul>
        <li>
          <strong>Studio:</strong> właścicielka studia, upoważnieni pracownicy
          oraz instruktorzy prowadzący zajęcia (lista uczestników ich zajęć).
        </li>
        <li>
          <strong>Inni użytkownicy:</strong> przy wyszukiwaniu znajomych widoczne
          jest Twoje imię i nazwisko oraz częściowo ukryty adres e-mail. Twoi
          zaakceptowani znajomi widzą Twoje imię i nazwisko, wpisy na Tablicy
          oraz wspólne zajęcia. Nie publikujemy Twoich danych publicznie.
        </li>
      </ul>
    </Section>

    <Section title="6. Komu przekazujemy dane">
      <p>
        Nie sprzedajemy danych i nie przekazujemy ich w celach marketingowych.
        Korzystamy z następujących dostawców (podmiotów przetwarzających lub
        samodzielnych administratorów w zakresie ich usług):
      </p>
      <ul>
        <li>
          <strong>Google (Firebase Authentication, Firebase Cloud Messaging,
          Google Analytics dla Firebase, Google Workspace / Gmail, Google
          Drive)</strong> – logowanie i konta użytkowników, powiadomienia push
          na Androidzie, statystyki korzystania z aplikacji, wysyłka e-maili,
          przechowywanie kopii zapasowych.
        </li>
        <li>
          <strong>Apple (Sign in with Apple, Apple Push Notification
          service)</strong> – logowanie przez Apple i powiadomienia push na
          iOS.
        </li>
        <li>
          <strong>PayU S.A.</strong> (Poznań) – obsługa płatności online.
          PayU jest samodzielnym administratorem danych podanych podczas
          płatności.
        </li>
        <li>
          <strong>Dostawca serwera (hosting VPS)</strong> – przechowywanie bazy
          danych i działanie Serwisu.
        </li>
        <li>
          Organy publiczne i biuro rachunkowe – wyłącznie, gdy wymagają tego
          przepisy prawa.
        </li>
      </ul>
    </Section>

    <Section title="7. Przekazywanie danych poza EOG">
      <p>
        Część dostawców (Google, Apple) może przetwarzać dane w Stanach
        Zjednoczonych. Odbywa się to na podstawie decyzji Komisji Europejskiej
        stwierdzającej odpowiedni stopień ochrony (EU-US Data Privacy
        Framework) lub standardowych klauzul umownych zatwierdzonych przez
        Komisję Europejską.
      </p>
    </Section>

    <Section title="8. Jak długo przechowujemy dane">
      <ul>
        <li>Dane konta – do czasu usunięcia konta.</li>
        <li>
          Historia zajęć, karnetów i płatności – przez okres wymagany przepisami
          podatkowymi i rachunkowymi (co do zasady 5 lat od końca roku
          podatkowego), a po usunięciu konta w formie zanonimizowanej.
        </li>
        <li>Tokeny powiadomień push – do wylogowania lub usunięcia konta.</li>
        <li>Statystyki korzystania z aplikacji – 14 miesięcy.</li>
        <li>
          Logi serwera i kopie zapasowe – nadpisywane cyklicznie, w praktyce
          przez kilka tygodni.
        </li>
      </ul>
    </Section>

    <Section title="9. Usunięcie konta">
      <p>
        Konto możesz usunąć samodzielnie w aplikacji: <em>Profil → Usuń
        konto</em>, albo pisząc na adres{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> z adresu
        przypisanego do konta.
      </p>
      <p>
        Po usunięciu konta kasujemy Twoje konto logowania, przyszłe zapisy,
        miejsca na listach rezerwowych, znajomych i zgody na wspólne zapisy,
        wpisy i reakcje na Tablicy oraz tokeny powiadomień. Imię i nazwisko,
        adres e-mail oraz numer telefonu są usuwane z naszej bazy. Zachowujemy
        jedynie zanonimizowaną historię zajęć, karnetów i płatności, wymaganą
        do rozliczeń studia. Konta nie można usunąć, dopóki są nieopłacone
        zaległości za zajęcia.
      </p>
    </Section>

    <Section title="10. Twoje prawa">
      <p>Masz prawo do:</p>
      <ul>
        <li>dostępu do swoich danych i otrzymania ich kopii,</li>
        <li>sprostowania danych,</li>
        <li>usunięcia danych,</li>
        <li>ograniczenia przetwarzania,</li>
        <li>przenoszenia danych,</li>
        <li>
          sprzeciwu wobec przetwarzania opartego na prawnie uzasadnionym
          interesie,
        </li>
        <li>
          wniesienia skargi do Prezesa Urzędu Ochrony Danych Osobowych
          (ul. Stawki 2, 00-193 Warszawa).
        </li>
      </ul>
      <p>
        Aby skorzystać z praw, napisz na{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Nie
        podejmujemy wobec Ciebie decyzji opartych wyłącznie na
        zautomatyzowanym przetwarzaniu, w tym profilowaniu.
      </p>
    </Section>

    <Section title="11. Pliki cookies i pamięć przeglądarki">
      <p>
        Strona internetowa nie używa narzędzi analitycznych ani reklamowych
        (statystyki opisane w punkcie 2 zbiera tylko aplikacja mobilna).
        Zapisujemy w przeglądarce wyłącznie informacje niezbędne do jej
        działania:
      </p>
      <ul>
        <li>cookie z informacją, że zaakceptowano komunikat o cookies (1 rok),</li>
        <li>sesję logowania Firebase (do wylogowania),</li>
        <li>
          stan rozpoczętej płatności i ukrytych komunikatów (pamięć lokalna
          przeglądarki).
        </li>
      </ul>
      <p>
        Strona pobiera czcionki z serwerów Google Fonts, co wiąże się z
        przekazaniem Google Twojego adresu IP. Cookies możesz usunąć lub
        zablokować w ustawieniach przeglądarki; zablokowanie pamięci
        przeglądarki uniemożliwi zalogowanie się.
      </p>
    </Section>

    <Section title="12. Dzieci">
      <p>
        Serwis nie jest skierowany do dzieci poniżej 16 roku życia. Konto
        dla osoby niepełnoletniej powinien założyć rodzic lub opiekun prawny.
      </p>
    </Section>

    <Section title="13. Zmiany polityki">
      <p>
        O istotnych zmianach tej polityki poinformujemy na stronie i w
        aplikacji. Aktualna wersja jest zawsze dostępna pod adresem{" "}
        <a href="https://www.natchnieniestudio.pl/polityka-prywatnosci">
          www.natchnieniestudio.pl/polityka-prywatnosci
        </a>
        .
      </p>
    </Section>
  </div>
);

export default PrivacyPolicy;
